'use client';

import 'react-notion-x/src/styles.css';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { ExtendedRecordMap } from 'notion-types';
import { useEffect } from 'react';
import { NotionRenderer } from 'react-notion-x';
import { cn } from 'src/app/lib/utils/tailwindcss';

import { NotionLoading } from './NotionLoading';

// 동적 임포트로 필요한 컴포넌트들 로드 (PDF 제외)
const Collection = dynamic(() => import('react-notion-x/build/third-party/collection').then((m) => m.Collection));

const Equation = dynamic(() => import('react-notion-x/build/third-party/equation').then((m) => m.Equation));

const Modal = dynamic(() => import('react-notion-x/build/third-party/modal').then((m) => m.Modal), {
  ssr: false,
});

interface NotionPageClientProps {
  recordMap: ExtendedRecordMap;
  darkMode?: boolean;
  hidden?: {
    header?: boolean;
    title?: boolean;
    cover?: boolean;
    icon?: boolean;
  };
  rootDomain?: string;
  rootPageId?: string;
  previewImagesEnabled?: boolean;
}

interface WindowWithDebug {
  recordMap?: ExtendedRecordMap;
  block?: unknown;
}

export function NotionPageClient({
  recordMap,
  darkMode = false,
  hidden,
  rootDomain,
  rootPageId,
  previewImagesEnabled,
}: NotionPageClientProps) {
  // 개발 모드에서 recordMap을 window 객체에 저장 (디버깅용)
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      const keys = Object.keys(recordMap?.block || {});
      const block = recordMap?.block?.[keys[0]]?.value;
      (window as WindowWithDebug).recordMap = recordMap;
      (window as WindowWithDebug).block = block;
    }
  }, [recordMap]);

  if (!recordMap) {
    return <NotionLoading />;
  }

  const isAllHidden = hidden ? Object.values(hidden).every((v) => v) : false;

  return (
    <div className={cn('notion-page-wrapper', darkMode && 'dark-mode', isAllHidden && 'all-hidden')}>
      <NotionRenderer
        // Type assertion due to notion-types version mismatch (our 7.x vs react-notion-x's 6.x)
        recordMap={recordMap as unknown as Parameters<typeof NotionRenderer>[0]['recordMap']}
        fullPage
        darkMode={darkMode}
        rootDomain={rootDomain}
        rootPageId={rootPageId}
        previewImages={previewImagesEnabled}
        bodyClassName={cn(
          isAllHidden && 'all-hidden',
          hidden?.title && '[&_.notion-title]:!hidden',
          hidden?.cover && '[&_.notion-page-cover-wrapper]:!hidden',
          hidden?.icon && '[&_.notion-page-icon-hero]:!hidden',
        )}
        components={{
          nextLink: Link,
          Collection,
          Equation,
          Modal,
          // PDF 컴포넌트는 제외 (canvas 의존성 문제로 인해)
        }}
        mapPageUrl={(pageId) => pageId}
        disableHeader={!!hidden?.header}
      />
    </div>
  );
}
